import { useCallback, useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { GradeResult } from '@yuan-math/exercises';
import { workingLevel } from '@yuan-math/learning';
import {
  NORTH_TRIAL_ID,
  SOUTH_TRIAL_ID,
  battleReducer,
  currentQuestion,
  replayEncounter,
  trialCleared,
  trialFor,
  TRIAL_GOLD,
  TRIAL_XP,
  type EncounterQuestion,
  type EncounterRun,
} from '@yuan-math/world';
import { NotFoundPage } from '../NotFoundPage.tsx';
import { Card } from '@yuan-math/ui';
import { ExerciseRunner } from '../../components/ExerciseRunner.tsx';
import { bank, registry, useLearner } from '../../state/LearnerContext.tsx';
import { useRewards } from '../../state/RewardsContext.tsx';

/**
 * The trials at the ends of the two long roads.
 *
 * One page, two trials, because they differ in what they *ask* rather than in
 * how they are fought: North Reach goes deep — one concept per level, climbing
 * to the hardest rung a student has reached — and the Sunken Reach goes wide,
 * one concept per strand. Forking this file would put two copies of the same
 * fight loop in the codebase, and within a release they would disagree about
 * something small and important, which is the argument `encounter.ts` already
 * makes about bosses and warded sites.
 *
 * **This page is in front of the isle's lock, not behind it.** North Reach is
 * opened by `twenty-mastered` and that achievement withholds the parka and the
 * name — never this. There is no guard on this route and no readiness check on
 * this page: a student who has mastered nothing, walked the road out of
 * curiosity and tapped the sign gets a trial pitched at the mathematics they
 * have actually met. Mathematics on this platform is never behind a lock, an
 * island is not an exception, and a page that refused to build for a beginner
 * would be the whole archipelago quietly becoming the thing this codebase
 * refuses to be.
 *
 * **The difficulty is `workingLevel`, never XP.** Level and gold are habit, and
 * nothing on this platform unlocks on them. `workingLevel` is the one answer to
 * "what level is this student at" — stated level, else the placement estimate,
 * else the hardest thing they have done — and `northTrial` turns it into three
 * stages of their own mathematics. Within the fight the band is *adaptive*, so
 * it climbs when they are right and eases when they are not: two right to step
 * up, one wrong to step down, because it must be easier to be given help than a
 * harder problem.
 *
 * The fight itself is not stored. It is replayed from
 * `(learner, def, attempt, answers)` exactly as a boss and a warded site are,
 * which is what makes a reloaded trial the same trial — and every answer goes
 * through `recordAttempt` on the ordinary path, because a fight cannot be a
 * place where mathematics happens and is not recorded.
 */
/** What each road ends at. Presentation only — the fight is `trialFor`. */
const TRIALS: Record<string, { name: string; emoji: string; lead: string }> = {
  [NORTH_TRIAL_ID]: {
    name: 'The North Reach Trial',
    emoji: '❄️',
    lead: 'Three stages, drawn from the hardest mathematics you have reached. It gets harder as you get them right, and easier as you do not.',
  },
  [SOUTH_TRIAL_ID]: {
    name: 'The Sunken Reach Trial',
    emoji: '🏜️',
    lead: 'Four stages, one from each corner of the mathematics you have met. It gets harder as you get them right, and easier as you do not.',
  },
};

export function TrialPage(): JSX.Element {
  const { trialId = '' } = useParams();
  const { profile, recordAttempt, recordBattleAnswer, resetBattle, settleTrialVictory } = useLearner();
  const { celebrate } = useRewards();

  /*
   * Fixed for the visit, like every other fight page: `replayEncounter` records
   * `now` and never seeds from it, but a value that moved every render would
   * make a new object every render and the memo below would never hold.
   */
  const [openedAt] = useState(() => Date.now());

  /*
   * The level is frozen for the visit for the same reason `PracticePage`
   * freezes its seed and a warded site freezes its band: the concepts are part
   * of the question seed, and `workingLevel` *moves as the student answers*. A
   * definition rebuilt from a live level would re-draw the questions underneath
   * somebody halfway through them.
   */
  const [level] = useState(() =>
    profile ? workingLevel({ registry, profile, now: Date.now() }) : 0,
  );

  const trial = TRIALS[trialId];
  const def = useMemo(() => trialFor(trialId, registry, bank, level), [trialId, level]);

  const record = profile?.rpg?.battles?.[def?.id ?? ''];
  const attemptIndex = record?.attemptIndex ?? 0;
  const answers = useMemo(() => record?.answers ?? [], [record?.answers]);

  const run: EncounterRun | undefined = useMemo(
    () => (def && profile ? { def, bank, learnerId: profile.id, attemptIndex } : undefined),
    [def, profile?.id, attemptIndex],
  );
  const battle = useMemo(
    () => (run ? replayEncounter(run, answers, openedAt) : undefined),
    [run, answers, openedAt],
  );

  /** The answered question, held on screen so its explanation survives. */
  const [held, setHeld] = useState<EncounterQuestion | null>(null);
  const [advancedStage, setAdvancedStage] = useState<number | null>(null);
  const [firstClearHere, setFirstClearHere] = useState(false);
  const settled = useRef(false);

  const live = useMemo(() => {
    if (!battle) return undefined;
    return battle.outcome === 'stage-cleared' && advancedStage === battle.stageIndex
      ? battleReducer(battle, { kind: 'advance' })
      : battle;
  }, [battle, advancedStage]);

  const onGraded = useCallback(
    (result: GradeResult, meta: { responseMs: number; firstTry: boolean; usedHint: boolean }) => {
      if (!def || !live) return;
      if (result.status === 'malformed') return;
      const question = held ?? currentQuestion(live);
      if (!question) return;

      // Real evidence on the ordinary path — retries included, exactly as the
      // practice page records them.
      recordAttempt({
        conceptId: question.conceptId,
        exerciseId: question.exercise.id,
        difficulty: question.exercise.difficulty,
        result,
        responseMs: meta.responseMs,
        usedHint: meta.usedHint,
        firstTry: meta.firstTry,
      });

      // The trial sees the first answer only. A second try is worth having for
      // the explanation, but a fight scored on the last of several tries is an
      // assessment of nothing.
      if (!meta.firstTry) return;
      recordBattleAnswer({ battleId: def.id, attemptIndex, answer: result.correct ? 'correct' : 'wrong' });
      setHeld(question);
    },
    [attemptIndex, def, held, live, recordAttempt, recordBattleAnswer],
  );

  const pressOn = useCallback(() => setHeld(null), []);

  const onRetry = useCallback(() => {
    if (!def) return;
    resetBattle(def.id);
    setHeld(null);
    setAdvancedStage(null);
    setFirstClearHere(false);
    settled.current = false;
  }, [def, resetBattle]);

  const won = battle?.outcome === 'won' && held === null;
  useEffect(() => {
    if (!profile || !battle || !won || settled.current) return;
    settled.current = true;
    // Read before settling: afterwards the claim exists either way, so this is
    // the only moment that can tell a first clear from a re-fight.
    const first = !trialCleared(trialId, profile);
    settleTrialVictory(trialId, battle.outcome);
    if (!first) return;
    setFirstClearHere(true);
    celebrate({
      key: `trial-${trialId}`,
      tone: 'quest',
      icon: trial?.emoji ?? '🏅',
      title: `${trial?.name ?? 'The trial'} is behind you`,
      detail: `+${TRIAL_GOLD} gold`,
      xp: TRIAL_XP,
    });
  }, [battle, celebrate, profile, settleTrialVictory, trial, trialId, won]);

  if (!trial) return <NotFoundPage />;
  if (!profile) return <div />;

  const back = (
    <div className="trial-page__footer">
      <Link to="/adventure/map" className="btn btn--ghost btn--sm">
        Back to the map
      </Link>
      <p className="trial-page__note">
        Walking away costs nothing and keeps your place. Losing costs nothing either — the road is still there.
      </p>
    </div>
  );

  /*
   * Nothing to ask. Impossible with the shipped curriculum and
   * `content:validate` would catch it, but a fight that throws on an empty
   * stage list is worse than a page that says so plainly.
   */
  if (!def || !battle || !live) {
    return (
      <div className="page page--narrow trial-page">
        <h1 className="page__title">
          <span aria-hidden="true">{trial.emoji}</span> {trial.name}
        </h1>
        <Card>
          <p>There is nothing to ask here yet. The road, at least, was worth walking.</p>
        </Card>
        {back}
      </div>
    );
  }

  const cleared = trialCleared(NORTH_TRIAL_ID, profile);
  const question = held ?? currentQuestion(live);

  return (
    <div className="page page--narrow trial-page">
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <Link to="/adventure/map">World map</Link>
        <span aria-hidden="true">/</span>
        <span>{trial.name}</span>
      </nav>

      <header className="trial-page__header">
        <h1 className="page__title">
          <span aria-hidden="true">{trial.emoji}</span> {trial.name}
        </h1>
        {/*
          What it is pitched at, said plainly. A trial that scaled to a student
          without telling them would be a difficulty knob they could not see,
          and the honest sentence costs one line.
        */}
        <p className="page__lead">{trial.lead}</p>
      </header>

      {battle.outcome === 'won' ? (
        <Card className="trial-page__result">
          <h2>{firstClearHere ? 'Cleared.' : 'Cleared again.'}</h2>
          <p>
            {def.stages.length} stages, at the end of a long road.{' '}
            {firstClearHere ? `+${TRIAL_XP} XP and ${TRIAL_GOLD} gold.` : 'Nothing more to pay — you have had it once.'}
          </p>
          <button type="button" className="btn btn--ghost" onClick={onRetry}>
            Walk it again
          </button>
          {back}
        </Card>
      ) : battle.outcome === 'lost' ? (
        <Card className="trial-page__result">
          <h2>Not this time.</h2>
          <p>Nothing is lost by it and nothing is taken. The trial is here whenever you want it again.</p>
          <button type="button" className="btn btn--primary" onClick={onRetry}>
            Try again
          </button>
          {back}
        </Card>
      ) : battle.outcome === 'stage-cleared' && advancedStage !== battle.stageIndex ? (
        <Card className="trial-page__interlude">
          <h2>Stage {battle.stageIndex + 1} behind you.</h2>
          <button type="button" className="btn btn--primary" onClick={() => setAdvancedStage(battle.stageIndex)}>
            Press on
          </button>
        </Card>
      ) : question ? (
        <>
          <p className="trial-page__stage">
            Stage {live.stageIndex + 1} of {def.stages.length} · {def.stages[live.stageIndex]?.name}
          </p>
          <ExerciseRunner exercise={question.exercise} onGraded={onGraded} showNext onNext={pressOn} />
          {back}
        </>
      ) : (
        back
      )}

      {cleared && battle.outcome === 'fighting' ? (
        <p className="trial-page__note">You have cleared this before. Fighting it again pays nothing and costs nothing.</p>
      ) : null}
    </div>
  );
}
